import { MouseEvent, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import store, { AppDispatch, RootState } from "../../../../redux/store";
import {
  ArrowDownload20Filled,
  CheckmarkCircle24Filled,
  Delete24Regular,
  Document20Regular,
  Eye24Regular,
  MoreVerticalRegular,
} from "@fluentui/react-icons";
import {
  Button,
  CompoundButton,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Skeleton,
  SkeletonItem,
  Tooltip,
  typographyStyles,
} from "@fluentui/react-components";

import moment from "moment";
import { getTimeDifference } from "../../../../utils";
import {
  fetchCustomerMessages,
  deleteMessage,
} from "../../../../redux/clientPortal/clientPortalslice";
import { MessageItem } from "../../../../types/portal";
import PortalDeletePopup from "../../../../components/DeletePopup/portalDeletePopup";
import ViewMessage from "../Messages/viewMessage";

import apiServices from "../../../../service";

const useStyles = makeStyles({
  text: { ...typographyStyles.subtitle2, color: "red" },
  name: { ...typographyStyles.subtitle1 },
  subject: {
    ...typographyStyles.subtitle2,
    whiteSpace: "nowrap",
  },
  document: { ...typographyStyles.body1 },
});

export default function SentDocuments({
  customerId,
  notify,
  sentDocuments,
}: any) {
  const styles = useStyles();
  const dispatch = useDispatch<AppDispatch>();

  const [openMessage, setOpenMessage] = useState<boolean>(false);
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const [messageDetails, setMessageDetails] = useState<any>({});
  const [actionProcessing, setActionProcessing] = useState(false);

  const { loading } = useSelector((state: RootState) => state.portal);
  // const sentDocuments = documents.sent;

  const handleView = useCallback((messageId: string, msg: MessageItem) => {
    if (messageId) {
      setMessageDetails({
        ...msg,
      });
      setOpenMessage(true);
    }
  }, []);

  const handleDownload = (doc: any, file: any) => {
    const link = document.createElement("a");
    link.href = file.FileLink;

    link.download = file.DocumentTitle;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteClick = useCallback((messageId: string) => {
    setCurrentMessageId(messageId);
    setDeletePopupOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    setActionProcessing(true);

    if (currentMessageId) {
      dispatch(deleteMessage(currentMessageId))
        .then((res) => {
          notify(res.payload.message, "", "success");
          if (customerId) {
            dispatch(fetchCustomerMessages({ customerId }));
          }
          setActionProcessing(false);
        })
        .catch((err) => {
          console.error("Error deleting message:", err);
          setActionProcessing(false);
        });
      setDeletePopupOpen(false);
      setCurrentMessageId(null);
    }
  }, [dispatch, customerId, currentMessageId]);

  return (
    <>
      {loading ? (
        Array.from({ length: 5 }).map((__, index) => (
          <div key={index} className="invertedWrapper">
            <Skeleton
              style={{ width: "100%" }}
              className=""
              appearance="translucent"
            >
              <SkeletonItem
                style={{ width: "15%", height: "20px", margin: "5px 0" }}
              />
              <SkeletonItem
                style={{ width: "100%", height: "20px", margin: "5px 0" }}
              />
              <SkeletonItem
                style={{ width: "40%", height: "20px", margin: "5px 0" }}
              />
            </Skeleton>
          </div>
        ))
      ) : sentDocuments?.length ? (
        sentDocuments.map((doc: any) => {
          const formattedTooltip = moment(doc.CreatedAt).format(
            "DD/MM/YYYY HH:mm a"
          );
          const relativeTime = getTimeDifference(doc.CreatedAt);
          const readTime = doc.ReadAt
            ? moment(doc.ReadAt).format("DD/MM/YYYY HH:mm a")
            : "";

          return (
            <div
              key={doc.MessageId}
              onClick={() => handleView(doc.MessageId, doc)}
            >
              <div className="msg_box" style={{ color: "#000000a6" }}>
                <div className="w-full">
                  <div className="justify_between">
                    <p className={`${styles.subject} lineClamp`}>
                      {doc.Subject}
                    </p>

                    <Tooltip
                      withArrow
                      appearance="inverted"
                      positioning="below"
                      content={formattedTooltip}
                      relationship="label"
                    >
                      <p className={styles.document}>{relativeTime}</p>
                    </Tooltip>
                  </div>
                  <div>
                    <p className={`${styles.document} lineClamp`}>
                      {doc.Message}
                    </p>
                  </div>
                  <div className="document_list">
                    {doc.attachments?.slice(0, 2).map((file: any) => (
                      <div
                        key={file.DocumentId}
                        className="custom-tag"
                        style={{
                          background: file.IsDownloaded
                            ? "#f9f9f9"
                            : "transparent",
                        }}
                      >
                        <Document20Regular
                          style={{
                            color: file.IsDownloaded ? "#205393" : "#616161",
                          }}
                        />
                        <p style={{ color: "#000" }}>{file.DocumentTitle}</p>
                        {file.IsDownloaded ? (
                          <Tooltip
                            withArrow
                            appearance="inverted"
                            positioning="below"
                            content={
                              file.IsDownloaded && file.DownloadAt
                                ? `Downloaded at: ${moment(
                                    file.DownloadAt
                                  ).format("DD/MM/YYYY HH:mm a")}`
                                : "Downloaded"
                            }
                            relationship="label"
                          >
                            <ArrowDownload20Filled
                              style={{ color: "#205393" }}
                              onClick={(event: any) => {
                                event.stopPropagation();
                                handleDownload(doc, file);
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <ArrowDownload20Filled
                            onClick={(event: any) => {
                              event.stopPropagation();
                              handleDownload(doc, file);
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="justify_between">
                  {doc.Read ? (
                    <Tooltip
                      withArrow
                      appearance="inverted"
                      positioning="below"
                      content={doc.Read && `Read at: ${readTime}`}
                      relationship="label"
                    >
                      <CheckmarkCircle24Filled style={{ color: "#205393" }} />
                    </Tooltip>
                  ) : (
                    <CheckmarkCircle24Filled style={{ color: "#000000a6" }} />
                  )}

                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button
                        appearance="subtle"
                        icon={<MoreVerticalRegular />}
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation();
                          setCurrentMessageId(doc.MessageId);
                        }}
                      />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem
                          icon={<Eye24Regular />}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleView(doc.MessageId, doc);
                          }}
                        >
                          View
                        </MenuItem>

                        {/* <MenuItem

                          icon={<Delete24Regular />}
                          onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            handleDeleteClick(doc.MessageId);
                          }}
                        >
                          Delete
                        </MenuItem> */}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="data_body no_data">
          <p className={styles.text}>There are no documents</p>
        </div>
      )}
      <ViewMessage
        open={openMessage}
        onClose={() => setOpenMessage(false)}
        messageDetails={messageDetails}
        customerId={customerId}
      />
      <PortalDeletePopup
        open={deletePopupOpen}
        onClose={() => setDeletePopupOpen(false)}
        onConfirmDelete={handleConfirmDelete}
        actionProcessing={actionProcessing}
      />
    </>
  );
}
